import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, ArrowLeft } from "lucide-react";

export default function AdminResetPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);

  const requestOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/password/request-otp", { email });
      toast.success("OTP sent to your email");
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send OTP");
    }
    setBusy(false);
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await api.post("/admin/password/reset", { email, otp, new_password: pwd });
      toast.success("Password updated — sign in with your new password");
      nav("/admin/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16 bg-ink-900 text-cream-100 -mx-6">
      <form onSubmit={step === 1 ? requestOtp : doReset} className="w-full max-w-md bg-ink-900 border border-gold-500/30 p-10">
        <img src="/logo-black-1.png" alt="IMMUSKI" style={{ height: 60 }} className="mb-6 mx-auto" />
        <div className="flex items-center gap-2 eyebrow text-gold-400"><KeyRound size={12} /> Reset password</div>
        <h1 className="mt-3 font-display text-3xl">Admin password reset</h1>
        <p className="mt-2 text-sm text-cream-100/60">
          {step === 1 ? "Enter your admin email and we'll send a 6-digit code." : "Enter the code we sent and your new password."}
        </p>
        <div className="mt-8 space-y-4">
          <div>
            <Label className="text-cream-100/80">Admin email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={step === 2}
              className="mt-2 rounded-none bg-ink-900 border-gold-500/30 text-cream-100" data-testid="reset-email" />
          </div>
          {step === 2 && (
            <>
              <div>
                <Label className="text-cream-100/80">6-digit code</Label>
                <Input value={otp} onChange={(e) => setOtp(e.target.value)} maxLength={6}
                  className="mt-2 rounded-none bg-ink-900 border-gold-500/30 text-cream-100 tracking-[0.4em] font-mono" data-testid="reset-otp" />
              </div>
              <div>
                <Label className="text-cream-100/80">New password (min 8 chars)</Label>
                <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
                  className="mt-2 rounded-none bg-ink-900 border-gold-500/30 text-cream-100" data-testid="reset-pwd" />
              </div>
            </>
          )}
        </div>
        <button type="submit" disabled={busy} className="mt-8 w-full bg-gold-500 text-ink-900 py-3 text-xs uppercase tracking-[0.28em] hover:bg-gold-400 disabled:opacity-50" data-testid="reset-submit">
          {busy ? "Working…" : (step === 1 ? "Send code" : "Update password")}
        </button>
        <Link to="/admin/login" className="mt-4 flex items-center justify-center gap-1 text-[11px] uppercase tracking-widest text-cream-100/60 hover:text-gold-400">
          <ArrowLeft size={11} /> Back to sign in
        </Link>
      </form>
    </div>
  );
}
